import { useEffect, useState } from 'react'
import { getSecondsRemaining } from './utils'

const TICK_INTERVAL_IN_MS = 1000

/**
 * Seconds remaining until `expiresAt`, re-evaluated every second.
 *
 * Deliberately uses a plain `setInterval` rather than `useInterval` from
 * `@backoffice-frontend/hooks`: that hook delegates to `usePolling`, which
 * no-ops when polling is disabled (E2E and PR builds), which would silently
 * freeze the countdown.
 *
 * The value is recomputed from `expiresAt` on every tick instead of being
 * decremented, so a throttled/backgrounded tab does not drift.
 */
export const useCountdown = (expiresAt: string): number => {
  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    getSecondsRemaining(expiresAt, new Date()),
  )

  useEffect(() => {
    setSecondsRemaining(getSecondsRemaining(expiresAt, new Date()))

    const intervalId = setInterval(() => {
      const remaining = getSecondsRemaining(expiresAt, new Date())
      setSecondsRemaining(remaining)

      if (remaining <= 0) {
        clearInterval(intervalId)
      }
    }, TICK_INTERVAL_IN_MS)

    return () => clearInterval(intervalId)
  }, [expiresAt])

  return secondsRemaining
}
